/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { page as SamplePage } from '../../help/sample_pages/CardPage';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Card';

const CardManager: ComponentManagerFn<'Card'> = (
  platform: Platform,
): ComponentManager<'Card'> => {
  return {
    ...baseComponentManager<'Card'>(platform, 'Card'),
    defaultValue,
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'CardHeader',
            'CardContent',
            'CardActions',
            'TriggerOnClick',
            'PulseTip',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    props: {
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: false,
      },
      raised: { label: 'Raised', type: 'boolean', category: 'Look & Feel' },
      border: { label: 'Border', type: 'boolean', category: 'Look & Feel' },
      collapsible: {
        label: 'Collapsible',
        type: 'boolean',
        category: 'Look & Feel',
        helpText:
          'Select this property if collapsible is needed for card content to toggle.',
      },
      collapsed: {
        label: 'Collapsed',
        type: 'boolean',
        category: 'Look & Feel',
        helpText: 'Select this property to collapse card content',
      },
      cardHeight: {
        label: 'Height',
        type: 'string',
      },
      showOnlyOnHoverItemIds: {
        label: 'Show Only On Hover',
        type: 'MultiItemID',
        validateItemId: (value, { platform: p, pageId, appUid, itemId }) => {
          const found = p.findParent(
            appUid,
            pageId,
            value,
            (c) => c.itemId === itemId,
          );
          if (!found) {
            return `Unable to find a child component with this item id '${value}' inside this Card!`;
          }
          return '';
        },
        helpText:
          'Choose one or more Item IDs of the child components to be only visible on the mouse hover of this Card component.',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'border':
          return !itemData.raised;
        default:
          return true;
      }
    },
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    noOverlay: true,
    defaultChildren: ['CardHeader', 'CardContent', 'CardActions'],
    onChildMoveIn: ({ page, value }) => {
      // CardHeader must be the first child
      // CardActions must be the last child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'CardHeader' || bItem.type === 'CardActions') {
            return -1;
          }
          if (bItem.type === 'CardHeader' || aItem.type === 'CardActions') {
            return 1;
          }
        }
        return 0;
      });
    },
    icon: { iconName: 'io-card', iconType: 'cloudio' },
    description:
      'Card is a flexible and extensible content container. It includes options of header, actions and body.',
    helpRenders: [
      {
        title: 'Basic Card',
        description:
          'A simple card with a header (with Avatar & Icon Button), actions (footer) and empty body.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 400,
        visibleProps: ['border', 'raised'],
        item: {
          children: ['cardHeader', 'cardContent', 'cardActions'],
          itemId: 'card',
          parent: 'root',
          props: {},
          type: 'Card',
        },
      },
    ],
  };
};

export default CardManager;
